import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@material-ui/core';

import api from '../../../../../../services/api';

import * as S from '../iFood/styles';
import { ButtonStyled } from '../../styles';
import { useToast } from '../../../../../../hooks/toast';
import { useAuth } from '../../../../../../hooks/auth';

const PAGBANK_TYPE = 3;
const PAGBANK_DESCRIPTION = 'PAGBANK';

const PagBank: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const location = useLocation();

  const [credentialId, setCredentialId] = useState<number>();
  const [hasToken, setHasToken] = useState(false);
  const [hasRefreshToken, setHasRefreshToken] = useState(false);
  const [hasPublicKey, setHasPublicKey] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const connected = hasToken && hasRefreshToken;

  const loadIntegration = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/companies/${user.empresa_id}/integrations`,
      );

      const pagbank = data.find(
        (integration: any) =>
          integration.type === PAGBANK_TYPE ||
          integration.description === PAGBANK_DESCRIPTION,
      );

      setCredentialId(pagbank?.id);
      setHasToken(Boolean(pagbank?.token));
      setHasRefreshToken(Boolean(pagbank?.refreshToken));
      setHasPublicKey(Boolean(pagbank?.key));
    } catch (err) {
      addToast({
        title: 'Erro ao carregar integração PagBank',
        type: 'error',
      });
    }
  }, [user.empresa_id, addToast]);

  useEffect(() => {
    loadIntegration();
  }, [loadIntegration]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('pagbank');

    if (!status) {
      return;
    }

    if (status === 'connected') {
      addToast({
        title: 'PagBank conectado.',
        type: 'success',
      });
      loadIntegration();
    } else if (status === 'error') {
      addToast({
        title: params.get('message') || 'Falha ao conectar o PagBank',
        type: 'error',
      });
    }
  }, [addToast, loadIntegration, location.search]);

  const handleConnect = useCallback(async () => {
    try {
      setConnecting(true);
      const { data } = await api.get(
        `/companies/${user.empresa_id}/pagbank/connect`,
      );

      if (!data?.url) {
        throw new Error('URL de autorização não retornada');
      }

      window.location.href = data.url;
    } catch (err) {
      setConnecting(false);
      addToast({
        title: 'Erro ao iniciar conexão PagBank',
        type: 'error',
      });
    }
  }, [addToast, user.empresa_id]);

  const handleDisable = useCallback(async () => {
    if (!credentialId) {
      return;
    }

    try {
      await api.delete(
        `/companies/${user.empresa_id}/integrations/${credentialId}`,
      );
      setCredentialId(undefined);
      setHasToken(false);
      setHasRefreshToken(false);
      setHasPublicKey(false);
      addToast({
        title: 'Integração PagBank desabilitada.',
        type: 'success',
      });
    } catch (err) {
      addToast({
        title: 'Erro ao desabilitar o PagBank',
        type: 'error',
      });
    }
  }, [user.empresa_id, credentialId, addToast]);

  return (
    <S.Container>
      <h1>PagBank</h1>
      <p className="description">
        Cada loja autoriza a própria conta PagBank. Clique em conectar para
        abrir o PagBank, faça login na conta Vendedor/Empresarial da loja e
        aprove as permissões. A chave pública do cartão é obtida
        automaticamente nessa autorização.
      </p>

      {connected ? (
        <>
          <p className="description">
            {hasPublicKey
              ? 'Conta PagBank conectada. Cartão e PIX prontos para o checkout.'
              : 'Conta PagBank conectada, mas a chave de cartão não foi obtida. Reconecte para habilitar cartão no checkout.'}
          </p>
          {!hasPublicKey && (
            <ButtonStyled
              type="button"
              onClick={handleConnect}
              disabled={connecting}
              style={{ maxWidth: 320, marginBottom: 16 }}
            >
              Reconectar com PagBank
            </ButtonStyled>
          )}
          <Button
            style={{
              backgroundColor: '#ff0000',
              color: '#ffffff',
              maxWidth: 300,
              marginBottom: 24,
            }}
            onClick={handleDisable}
          >
            Desabilitar o PagBank
          </Button>
        </>
      ) : (
        <>
          {hasToken && !hasRefreshToken && (
            <p className="description">
              Esta loja ainda usa um token antigo. Reconecte pelo PagBank para
              gerar o Bearer da conta.
            </p>
          )}
          <ButtonStyled
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            style={{ maxWidth: 320, marginBottom: 24 }}
          >
            {hasToken ? 'Reconectar com PagBank' : 'Conectar com PagBank'}
          </ButtonStyled>
        </>
      )}
    </S.Container>
  );
};

export default PagBank;
